import {
  AutocompleteWithLabel,
  CountrySelectorForHeader,
  FreeSoloAutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Grid, MenuItem, Switch, Typography } from '@material-ui/core'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useEffect, useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import { BOARD_TYPES_QUERY } from '../../graphql/boardTypes'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { countries, CountryType } from '../../helpers/countriesList'
import { entityCodeRegex } from '../../helpers/regex'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { ProviderBrief } from '../../types/provider'
import { RatePlanBrief, RatePlanForGenerate } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'

interface RateplanBasicDetailsProps {
  values: RatePlanForGenerate
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<RatePlanForGenerate>
  touched: FormikTouched<RatePlanForGenerate>
  handleBlur: any
  isSubmitting: any
  roomTypes: RoomTypeBrief[]
  setRoomTypeCode: (rt: string) => void
  isPhysical: boolean
  propertyCode: string
  loadingForRoomTypes: boolean
  classes: any
}

export const RateplanBasicDetailsForGenerate: React.FC<
  RateplanBasicDetailsProps
> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  roomTypes,
  setRoomTypeCode,
  isPhysical,
  propertyCode,
  classes,
  loadingForRoomTypes
}) => {
  const [autoCompleteForProvider, setAutoCompleteForProvider] =
    useState<any>(null)
  const [selectedCountryForName, setselectedCountryForName] =
    useState<CountryType>(countries[0])
  const [selectedCountryForPolicy, setSelectedCountryForPolicy] =
    useState<CountryType>(countries[0])
  const [autoCompleteForRoomType, setAutoCompleteForRoomType] = useState<any>()
  const [autoCompleteForRatePlan, setAutoCompleteForRatePlan] = useState<any>()

  const { data, loading } = useQuery<{ boardTypes: string[] }>(
    BOARD_TYPES_QUERY
  )

  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only'
  })
  const [getRatePlans, { data: ratePlanData, loading: loadingForRatePlan }] =
    useLazyQuery<{
      ratePlans: RatePlanBrief[]
    }>(RATEPLANS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    })

  useEffect(() => {
    if (loadingForRoomTypes) {
      return
    }
    setRoomTypeCode(
      roomTypes?.find(
        (roomType) =>
          roomType.displayRoomTypeCode === values.basicDetails.roomTypeCode
      )?.displayRoomTypeCode || ''
    )
  }, [values.basicDetails.roomTypeCode, loadingForRoomTypes, roomTypes])

  useEffect(() => {
    if (propertyCode && autoCompleteForRoomType) {
      getRatePlans({
        variables: {
          propertyCode: propertyCode,
          roomTypeCode: autoCompleteForRoomType.value,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: false
        }
      })
    }
  }, [propertyCode, autoCompleteForRoomType])

  // useEffect(() => {
  //   if (values.basicDetails.type === 'Virtual') {
  //     setFieldValue('basicDetails.boardType', '')
  //   }
  // }, [values.basicDetails.type])

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  return (
    <div>
      <div className={classes.titleContainer}>
        <SectionTitle title="Basic Details" marginBottom />
      </div>
      <div className={classes.fieldsContainer}>
        <SelectWithLabel
          name="basicDetails.type"
          label={getTranslate('Type')}
          noHelperText
          fullWidth
          value={values.basicDetails.type}
          onChange={(event) =>
            setFieldValue('basicDetails.type', event.target.value)
          }
          error={
            (touched.basicDetails?.type && !!errors.basicDetails?.type) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.type
              ? getTranslate(errors.basicDetails?.type || '')
              : undefined
          }
          size="small">
          <MenuItem key="Physical" value="Physical">
            {getTranslate('Physical')}
          </MenuItem>
          <MenuItem key="Virtual" value="Virtual">
            {getTranslate('Virtual')}
          </MenuItem>
        </SelectWithLabel>
        <AutocompleteWithLabel
          label={getTranslate(isPhysical ? 'Room Type' : 'Parent Room Type')}
          placeholder={getTranslate(
            isPhysical ? 'Select Room type' : 'Select Parent Room type'
          )}
          name="basicDetails.roomTypeCode"
          id="basicDetails.roomTypeCode"
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForRoomType({
                value: v.value,
                label: v.label,
                code: v.code,
                id: v.id
              })
              setFieldValue('basicDetails.roomTypeCode', v.code)
              setFieldValue('basicDetails.name', 'With Breakfast')
              setFieldValue(
                `basicDetails.nameTranslatedList[0].value`,
                `${v.id} - با صبحانه` //use id to pass name of roomtype here (label has code too!)
              )
              setRoomTypeCode(v.value)
            } else {
              setAutoCompleteForRoomType(null)
              setFieldValue('basicDetails.roomTypeCode', '')
              setRoomTypeCode('')
            }
          }}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          options={
            roomTypes
              .filter(
                (ele) => ele.type === (isPhysical ? 'Physical' : 'Virtual')
              )
              .map((item) => ({
                label: `${item.name} (${item.displayRoomTypeCode})`,
                value: isPhysical
                  ? item.displayRoomTypeCode
                  : item.parentRoomTypeCode!,
                code: item.displayRoomTypeCode,
                id: item.name
              })) || []
          }
          size="small"
          error={
            (touched.basicDetails?.roomTypeCode &&
              !!errors.basicDetails?.roomTypeCode) ||
            undefined
          }
          value={autoCompleteForRoomType}
          includeInputInList
          helpertext={
            touched.basicDetails?.roomTypeCode
              ? getTranslate(errors.basicDetails?.roomTypeCode || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          loading={loadingForRoomTypes}
          loadingText={getTranslate('Loading') + '...'}
        />
        <AutocompleteWithLabel
          label={getTranslate('Provider')}
          placeholder={getTranslate('Select Provider')}
          name="basicDetails.providerCode"
          id="basicDetails.providerCode"
          inputSize="small"
          value={autoCompleteForProvider}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForProvider({
                value: v.value,
                label: v.label
              })
              setFieldValue('basicDetails.providerCode', v.value)
            } else {
              setAutoCompleteForProvider(null)
              setFieldValue('basicDetails.providerCode', '')
            }
          }}
          options={
            providerData?.providers
              ? providerData?.providers.map((p) => ({
                  label: `${p.name} - (${p.displayProviderCode})`,
                  value: p.providerCode || ''
                }))
              : []
          }
          includeInputInList
          onBlur={handleBlur}
          disabled={isSubmitting}
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
          helpertext={
            touched.basicDetails?.providerCode
              ? getTranslate(errors.basicDetails?.providerCode || '')
              : undefined
          }
          error={
            (touched.basicDetails?.providerCode &&
              !!errors.basicDetails?.providerCode) ||
            undefined
          }
        />
      </div>
      <div className={classes.fieldsContainer}>
        {!isPhysical && (
          <AutocompleteWithLabel
            label={getTranslate('Parent Rate Plan')}
            placeholder={getTranslate('Select Parent Rate Plan')}
            name="basicDetails.parentRatePlanCode"
            id="basicDetails.parentRatePlanCode"
            onChange={(_, v: any) => {
              if (v) {
                setAutoCompleteForRatePlan({
                  value: v.value,
                  label: v.label,
                  code: v.code
                })
                setFieldValue('basicDetails.parentRatePlanCode', v.code)
              } else {
                setAutoCompleteForRatePlan(null)
                setFieldValue('basicDetails.parentRatePlanCode', '')
              }
            }}
            getOptionLabel={(option) => option.label || ''}
            getOptionSelected={(option, value) => option.value === value.value}
            options={
              ratePlanData?.ratePlans.map((item) => ({
                label: `${item.name} (${item.displayRatePlanCode})`,
                value: item.id || '',
                code: item.displayRatePlanCode
              })) || []
            }
            size="small"
            error={
              (touched.basicDetails?.parentRatePlanCode &&
                !!errors.basicDetails?.parentRatePlanCode) ||
              undefined
            }
            value={autoCompleteForRatePlan}
            includeInputInList
            helpertext={
              touched.basicDetails?.parentRatePlanCode
                ? getTranslate(errors.basicDetails?.parentRatePlanCode || '')
                : undefined
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
            inputSize="small"
            loading={loadingForRatePlan}
            loadingText={getTranslate('Loading') + '...'}
          />
        )}
        <FreeSoloAutocompleteWithLabel
          name="basicDetails.code"
          label={getTranslate('Code')}
          placeholder={getTranslate('Choose a Code for rate plan')}
          id="basicDetails.code"
          onChange={(_, v: any) => {
            const inputValue = v.map((item: any) => item.replace(/\s+/g, ''))

            const isValid = inputValue.every((val: string) =>
              entityCodeRegex.test(val)
            )

            if (isValid || inputValue === '') {
              setFieldValue('basicDetails.code', inputValue)
            }
          }}
          error={
            (touched.basicDetails?.code && !!errors.basicDetails?.code) ||
            undefined
          }
          value={values.basicDetails.code}
          options={['']}
          message={''}
          regexPattern={/.*/}
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={getTranslate('Name')}
          placeholder={getTranslate('Enter the rate plan name')}
          name="basicDetails.name"
          id="basicDetails.name"
          value={values.basicDetails.name}
          onChange={(event) =>
            setFieldValue('basicDetails.name', event.currentTarget.value)
          }
          error={
            (touched.basicDetails?.name && !!errors.basicDetails?.name) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.name
              ? getTranslate(errors.basicDetails?.name || '')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
        <InputWithLabel
          label={`${
            lang === 'en' ? selectedCountryForName.language : ''
          } ${getTranslate('Name')} ${
            lang === 'fa' && selectedCountryForName.code === 'IR'
              ? selectedCountryForName.nativeLanguage
              : lang === 'fa'
              ? selectedCountryForName.language
              : ''
          }`}
          placeholder={
            lang === 'fa' && selectedCountryForName.code === 'IR'
              ? `${getTranslate('Enter the rate plan name in')} ${
                  selectedCountryForName.nativeLanguage
                }`
              : `${getTranslate('Enter the rate plan name in')} ${
                  selectedCountryForName.language
                }`
          }
          id={`basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
            (item) => item.label === selectedCountryForName.label
          )}].value`}
          name={`basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
            (item) => item.label === selectedCountryForName.label
          )}].value`}
          value={
            values.basicDetails.nameTranslatedList.find(
              (item) => item.label === selectedCountryForName.label
            )?.value
          }
          onChange={(event: {
            currentTarget: { value: React.SetStateAction<string> }
          }) => {
            setFieldValue(
              `basicDetails.nameTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
                (item) => item.label === selectedCountryForName.label
              )}].value`,
              event.currentTarget.value
            )
          }}
          error={
            (touched.basicDetails?.nameTranslatedList &&
              !!errors.basicDetails?.nameTranslatedList) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.nameTranslatedList
              ? (
                  errors.basicDetails?.nameTranslatedList as FormikErrors<{
                    value: string
                  }>[]
                )
                  ?.filter((item) => item)
                  .map((item) => getTranslate(item.value || ''))
                  .join('')
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rightHeader={
            <CountrySelectorForHeader
              selectedCountry={selectedCountryForName}
              onChange={(item: CountryType) => {
                const translatedName = {
                  label: item.label,
                  nativeLabel: item.nativeLanguage,
                  value: ''
                }
                const tempTranslatedArr = [
                  ...values.basicDetails.nameTranslatedList,
                  translatedName
                ]
                setFieldValue(
                  'basicDetails.nameTranslatedList',
                  tempTranslatedArr
                )
                setselectedCountryForName(item)
              }}
            />
          }
        />
      </div>
      <div className={classes.fieldsContainer}>
        {isPhysical && (
          <>
            <SelectWithLabel
              label={getTranslate('Board Type')}
              placeholder={getTranslate('Select a Board Type')}
              name="basicDetails.boardType"
              id="basicDetails.boardType"
              value={values.basicDetails.boardType}
              onChange={(event) =>
                setFieldValue('basicDetails.boardType', event.target.value)
              }
              error={
                (touched.basicDetails?.boardType &&
                  !!errors.basicDetails?.boardType) ||
                undefined
              }
              helpertext={
                touched.basicDetails?.boardType
                  ? getTranslate(errors.basicDetails?.boardType || '')
                  : undefined
              }
              size="small">
              {(loading ? [] : data?.boardTypes)?.map((singleBoardType) => (
                <MenuItem key={singleBoardType} value={singleBoardType}>
                  {singleBoardType}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <div />
            <div />
          </>
        )}
      </div>
      <div className={classes.fieldsContainer}>
        <TextAreaWithLabel
          label={getTranslate('Rate Policy')}
          placeholder={getTranslate('Enter the rate plan Rate Policy')}
          value={values.basicDetails.ratePolicy}
          onChange={(event) =>
            setFieldValue('basicDetails.ratePolicy', event.currentTarget.value)
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rows={7}
          error={
            (touched.basicDetails?.ratePolicy &&
              !!errors.basicDetails?.ratePolicy) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.ratePolicy
              ? getTranslate(errors.basicDetails?.ratePolicy || '')
              : undefined
          }
        />
        <TextAreaWithLabel
          placeholder={
            lang === 'fa' && selectedCountryForPolicy.code === 'IR'
              ? `${getTranslate('Enter the rate plan Rate Policy in')} ${
                  selectedCountryForPolicy.nativeLanguage
                }`
              : `${getTranslate('Enter the rate plan Rate Policy in')} ${
                  selectedCountryForPolicy.language
                }`
          }
          label={`${
            lang === 'en' ? selectedCountryForPolicy.language : ''
          } ${getTranslate('Rate Policy')} ${
            lang === 'fa' && selectedCountryForPolicy.code === 'IR'
              ? selectedCountryForPolicy.nativeLanguage
              : lang === 'fa'
              ? selectedCountryForPolicy.language
              : ''
          }`}
          id={`basicDetails.ratePolicyTranslatedList[${values.basicDetails.ratePolicyTranslatedList.findIndex(
            (item) => item.label === selectedCountryForPolicy.label
          )}].value`}
          name={`basicDetails.ratePolicyTranslatedList[${values.basicDetails.ratePolicyTranslatedList.findIndex(
            (item) => item.label === selectedCountryForPolicy.label
          )}].value`}
          value={
            values.basicDetails.ratePolicyTranslatedList?.find(
              (item) => item.label === selectedCountryForPolicy.label
            )?.value
          }
          onChange={(event: {
            currentTarget: { value: React.SetStateAction<string> }
          }) => {
            setFieldValue(
              `basicDetails.ratePolicyTranslatedList[${values.basicDetails.nameTranslatedList.findIndex(
                (item) => item.label === selectedCountryForPolicy.label
              )}].value`,
              event.currentTarget.value
            )
          }}
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
          rows={7}
          error={
            (touched.basicDetails?.ratePolicyTranslatedList &&
              !!errors.basicDetails?.ratePolicyTranslatedList) ||
            undefined
          }
          helpertext={
            touched.basicDetails?.ratePolicyTranslatedList
              ? (
                  errors.basicDetails
                    ?.ratePolicyTranslatedList as FormikErrors<{
                    value: string
                  }>[]
                )
                  ?.filter((item) => item)
                  .map((item) => getTranslate(item.value || ''))
                  .join('')
              : undefined
          }
          rightHeader={
            <CountrySelectorForHeader
              selectedCountry={selectedCountryForPolicy}
              onChange={(item: CountryType) => {
                const translatedDescription = {
                  label: item.label,
                  nativeLabel: item.nativeLanguage,
                  value: ''
                }
                const tempTranslatedArr = [
                  ...values.basicDetails.ratePolicyTranslatedList,
                  values.basicDetails.ratePolicyTranslatedList.find(
                    (singleDescription) =>
                      singleDescription.label === item.label
                  )
                    ? undefined
                    : translatedDescription
                ]
                setFieldValue(
                  'basicDetails.ratePolicyTranslatedList',
                  tempTranslatedArr
                )
                setSelectedCountryForPolicy(item)
              }}
            />
          }
        />
        {isRoleAdmin && (
          <div className={classes.switchContainer}>
            <Typography variant="h4">
              {getTranslate('Is this ratePlan active?')}
            </Typography>
            <Typography component="div" className={classes.switch}>
              <Grid
                component="label"
                container
                alignItems="center"
                justify="flex-start">
                <Grid item>{getTranslate('No')}</Grid>
                <Grid item>
                  <Switch
                    name="active"
                    color="primary"
                    onChange={() => {}}
                    checked={true}
                    disabled={true}
                  />
                </Grid>
                <Grid item>{getTranslate('Yes')}</Grid>
              </Grid>
            </Typography>
          </div>
        )}
      </div>
    </div>
  )
}
